import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { DeliveryAddressBlock } from "@/components/companies/DeliveryAddressBlock";
import { ViewCompanyBillDocumentButton } from "@/components/companies/ViewCompanyBillDocumentButton";
import { cn } from "@/lib/utils";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type CompanyInboxBillResponse = components["schemas"]["CompanyInboxBillResponse"];
type OrganisationResponse = components["schemas"]["OrganisationResponse"];

interface AssignCompanyBillDialogProps {
  companyId: string;
  bill: CompanyInboxBillResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}

const scoreFmt = new Intl.NumberFormat("en-GB", { style: "percent", maximumFractionDigits: 0 });
const PAGE_SIZE = 50;

/** Routes one bill out of the company inbox. Only the company's own sites are offered (the backend rejects others),
 * searched server-side so no site is ever unreachable. */
export function AssignCompanyBillDialog({
  companyId,
  bill,
  open,
  onOpenChange,
  onAssigned,
}: AssignCompanyBillDialogProps) {
  const [organisations, setOrganisations] = useState<OrganisationResponse[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<OrganisationResponse | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const debouncedQuery = useDebouncedValue(query, 300);
  const searchTerm = debouncedQuery.trim();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(null);
  }, [open, bill?.id]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOrganisations(null);
    setLoadError(null);

    (async () => {
      const { data, error } = await api.GET("/v1/companies/{companyId}/organisations", {
        params: {
          path: { companyId },
          query: { Limit: PAGE_SIZE, ...(searchTerm ? { Name: searchTerm } : {}) },
        },
      });
      if (cancelled) return;

      // A failed request isn't a company with no sites; don't render both as an empty list.
      if (error) {
        setLoadError("Couldn't load this company's organisations.");
        setOrganisations([]);
        return;
      }
      setOrganisations(data?.data ?? []);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, companyId, searchTerm]);

  // Preselect the router's best guess, resolved by id: with a searchable list it may not be on the page shown.
  useEffect(() => {
    if (!open || !bill?.suggestedOrganisationId) return;
    let cancelled = false;
    (async () => {
      const { data } = await api.GET("/v1/organisations/{id}", {
        params: { path: { id: bill.suggestedOrganisationId! } },
      });
      if (!cancelled && data) setSelected(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bill?.id, bill?.suggestedOrganisationId]);

  const candidateScores = useMemo(
    () => new Map((bill?.candidates ?? []).map((c) => [c.organisationId, c] as const)),
    [bill?.candidates],
  );

  async function handleAssign() {
    if (!bill?.id || !selected?.id) return;
    setSubmitting(true);
    try {
      const { error, response } = await api.POST("/v1/companies/{companyId}/bills/{billId}/assign", {
        params: { path: { companyId, billId: bill.id } },
        body: { organisationId: selected.id },
      });
      if (error) {
        await toastApiError({ error, response }, "Couldn't assign the bill.");
        return;
      }
      toastApiSuccess("Bill assigned", "It is now that organisation's bill and is awaiting review.");
      onAssigned();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign bill to an organisation</DialogTitle>
          <DialogDescription>
            {bill?.supplierName ? `${bill.supplierName} - ` : ""}
            {bill?.invoiceNumber ?? "No invoice number"}
            {bill?.billPoNumbers?.length
              ? ` · Extracted PO: ${bill.billPoNumbers.join(", ")}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
            <div className="min-w-0 text-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                The document
              </div>
              <div className="mt-1 truncate">
                {bill?.fileName ?? "No document was attached to this bill"}
              </div>
            </div>
            <ViewCompanyBillDocumentButton
              companyId={companyId}
              bill={bill}
              label="View bill"
              className="shrink-0"
            />
          </div>

          {bill?.deliveryAddress || bill?.deliveryAddressDetail ? (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Delivery address on the bill
              </div>
              {/* Broken into lines: the reader matches the postcode against sites by eye. */}
              <div className="mt-1">
                <DeliveryAddressBlock bill={bill} />
              </div>
            </div>
          ) : (
            // The absent address is why this bill needs a person, so say so rather than show nothing.
            <p className="text-sm text-muted-foreground">
              No delivery address could be read from this bill, so there was nothing to match a site
              against. Open the document to see where it was delivered.
            </p>
          )}

          <div className="grid gap-2">
            <Label htmlFor="assign-organisation">Organisation</Label>
            {/* modal: inside a Dialog, whose scroll lock (react-remove-scroll) swallows wheel events on this portaled list. */}
            <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  id="assign-organisation"
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="w-full justify-between font-normal"
                >
                  {selected ? (
                    <span className="truncate">
                      {selected.name}
                      {selected.postalCode ? ` - ${selected.postalCode}` : ""}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Choose an organisation...</span>
                  )}
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
                {/* shouldFilter=false: the server does the matching. */}
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search this company's organisations..."
                    value={query}
                    onValueChange={setQuery}
                  />
                  <CommandList>
                    {organisations === null ? (
                      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                        <Spinner className="size-3.5" /> Loading...
                      </div>
                    ) : loadError ? (
                      <div className="py-6 text-center text-sm text-destructive">{loadError}</div>
                    ) : (
                      <>
                        <CommandEmpty>
                          {searchTerm
                            ? "No organisation matches that."
                            : "This company has no organisations yet."}
                        </CommandEmpty>
                        <CommandGroup>
                          {organisations.map((o) => {
                            const candidate = o.id ? candidateScores.get(o.id) : undefined;
                            return (
                              <CommandItem
                                key={o.id}
                                value={o.id ?? o.name}
                                onSelect={() => {
                                  setSelected(o);
                                  setPickerOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 size-4 shrink-0",
                                    selected?.id === o.id ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate">{o.name}</span>
                                  {o.postalCode && (
                                    <span className="truncate text-xs text-muted-foreground">
                                      {o.postalCode}
                                    </span>
                                  )}
                                </div>
                                {candidate?.score != null && (
                                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                                    {scoreFmt.format(Number(candidate.score))} match
                                  </span>
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Only this company's organisations can be chosen. Assigning is final: the bill becomes that
              organisation's and starts its normal review.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={submitting || !selected}>
            {submitting && <Spinner className="size-3.5" />} Assign bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
